import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { UserRole } from './db-row-types';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Detects if the current platform is macOS
 */
export function isMacOS(): boolean {
  if (typeof window === 'undefined') return false;
  return navigator.platform.toUpperCase().indexOf('MAC') >= 0;
}

/**
 * Formats keyboard shortcuts based on the current platform
 */
export function formatKeyboardShortcut(keys: string[]): string {
  const isMac = isMacOS();

  // Create platform-specific key combinations
  let platformKeys: string[];

  if (isMac) {
    // For Mac: use the keys as specified
    platformKeys = keys;
  } else {
    // For Windows: convert cmd to ctrl, and handle special cases
    platformKeys = keys.map(key => {
      if (key === 'cmd') return 'ctrl';
      if (key === 'alt' && keys.includes('cmd')) return 'shift'; // For row shortcut
      return key;
    });
  }

  return platformKeys
    .map(key => {
      switch (key.toLowerCase()) {
        case 'cmd':
        case 'command':
          return isMac ? '⌘' : 'Ctrl';
        case 'ctrl':
          return isMac ? '⌃' : 'Ctrl';
        case 'alt':
        case 'option':
          return isMac ? '⌥' : 'Alt';
        case 'shift':
          return isMac ? '⇧' : 'Shift';
        case '=':
          return '=';
        case '|':
          return '|';
        default:
          return key.toUpperCase();
      }
    })
    .join(isMac ? '' : ' + ');
}

export const mapRoleToLabel = (role: UserRole | null) => {
  const roles = {
    owner: 'Owner',
    editor: 'Editor',
    viewer: 'Viewer',
    analyst: 'Analyst',
  };

  return roles[role ?? 'viewer'];
};

export function getIsDevToolboxEnabled(): boolean {
  return process.env.NEXT_PUBLIC_DEV_TOOLBOX_ENABLED === 'true';
}

/**
 * Return base URL that can be used to construct fully-qualified self-links.
 * Will throw an error if no valid deployment URL is found in production.
 */
export function getBaseUrl(): string {
  if (process.env.NODE_ENV === 'development') {
    // Dev servers run on arbitrary ports, so in the browser the real origin is the only
    // correct base (e.g. for copied share links); SSR/API code keeps the default dev origin.
    return typeof window !== 'undefined' ? window.location.origin : 'https://localhost:3000';
  }

  const vercelUrl = process.env.NEXT_PUBLIC_VERCEL_BRANCH_URL
    ? `https://${process.env.NEXT_PUBLIC_VERCEL_BRANCH_URL}`
    : undefined;

  // prefer explicitly set NEXT_PUBLIC_SITE_URL value over Vercel system value
  const url = process.env.NEXT_PUBLIC_SITE_URL || vercelUrl;

  if (!url) {
    throw new Error('Base URL is not defined. Please set NEXT_PUBLIC_SITE_URL.');
  }

  return url;
}

export function buildAbsoluteUrl(path: string): string {
  const base = getBaseUrl();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalizedPath}`;
}

/**
 * Executes a function after the next tick of the event loop.
 *
 * Useful if we want to make sure that the DOM is updated before we execute the function.
 * @param fn - The function to execute.
 */
export function nextTick(fn: () => void) {
  setTimeout(() => {
    fn();
  });
}
